import { Fragment, ChangeEvent } from 'react'
import type { Book } from '@/types'
import { formatAuthors, formatCategories } from '../../formattingUtils'
import { FIELD_LIMITS } from '../../../../utils/validation'

type DuplicateStatus = 'exact' | 'partial' | 'new'

interface ImportBook extends Partial<Book> {
    id: string
    status: DuplicateStatus
}

interface ImportPreviewModalProps {
    isOpen: boolean
    onClose: () => void
    importData: ImportBook[]
    updateImportItem: (id: string, field: string, value: string) => void
    removeImportItem: (id: string) => void
    handleImportConfirm: () => void
    isImporting: boolean
    books: Book[]
    existingAuthors: string[]
    existingPublishers: string[]
    existingLocations: string[]
    existingSeries: string[]
    existingCategories: string[]
    existingLanguages: string[]
    FORMAT_OPTIONS: readonly string[]
    STATUS_OPTIONS: readonly string[]
    PROGRESS_OPTIONS: readonly string[]
}

export default function ImportPreviewModal({
    isOpen,
    onClose,
    importData,
    updateImportItem,
    removeImportItem,
    handleImportConfirm,
    isImporting,
    books,
    existingPublishers,
    existingLocations,
    existingSeries,
    existingCategories,
    existingLanguages,
    FORMAT_OPTIONS,
    STATUS_OPTIONS,
    PROGRESS_OPTIONS,
}: Readonly<ImportPreviewModalProps>) {
    if (!isOpen) return null

    return (
        <div className="modal-overlay animate-fade-in" onClick={() => !isImporting && onClose()} role="presentation">
            <div className="modal-content import-modal animate-fade-in-up" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" tabIndex={0} data-testid="import-preview-modal">
                <div className="modal-header">
                    <h3>Vista Previa de Importación</h3>
                    {!isImporting && (
                        <button className="close-btn" onClick={onClose}>
                            ×
                        </button>
                    )}
                </div>
                <div className="modal-body">
                    <div className="import-status-legend">
                        <span className="legend-item">
                            <span className="status-icon new">✨</span> Nuevo libro
                        </span>
                        <span className="legend-item">
                            <span className="status-icon warning">❓</span> Título duplicado (Diferente autor)
                        </span>
                        <span className="legend-item">
                            <span className="status-icon danger">❗</span> Ya en biblioteca (Duplicado exacto)
                        </span>
                    </div>

                    <div className="import-table-wrapper">
                        <table className="import-table">
                            <thead>
                                <tr>
                                    <th>Borrar</th>
                                    <th>Nuevo</th>
                                    <th className="th-title">Título</th>
                                    <th className="th-author">Autor</th>
                                    <th>Idioma</th>
                                    <th>Categoría</th>
                                    <th>Editorial</th>
                                    <th>Serie</th>
                                    <th>#</th>
                                    <th>Formato</th>
                                    <th>Estado</th>
                                    <th>Progreso</th>
                                    <th>Ubicación</th>
                                    <th>Notas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {importData.map((item) => {
                                    const matchingBooks = books.filter(
                                        (b) => b.title?.toLowerCase().trim() === item.title?.toLowerCase().trim()
                                    )

                                    return (
                                        <Fragment key={item.id}>
                                            <tr className={item.status === 'exact' ? 'row-duplicate' : ''}>
                                                <td className="td-actions">
                                                    <button className="table-delete" onClick={() => removeImportItem(item.id)}>
                                                        🗑️
                                                    </button>
                                                </td>
                                                <td className="td-status">
                                                    {(() => {
                                                        if (item.status === 'exact') return '❗'
                                                        if (item.status === 'partial') return '❓'
                                                        return '✨'
                                                    })()}
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={item.title || ''}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => updateImportItem(item.id, 'title', e.target.value)}
                                                        className="import-input"
                                                        style={{ minWidth: '25ch' }}
                                                        title={item.title || ''}
                                                        placeholder="Título del libro"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={formatAuthors(item.author)}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => updateImportItem(item.id, 'author', e.target.value)}
                                                        className="import-input"
                                                        list="author-options"
                                                        style={{ minWidth: '25ch' }}
                                                        title={formatAuthors(item.author)}
                                                        placeholder="Autor"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={item.language || ''}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => updateImportItem(item.id, 'language', e.target.value)}
                                                        className="import-input"
                                                        list="language-options"
                                                        style={{ minWidth: '10ch' }}
                                                        title={item.language || ''}
                                                        maxLength={FIELD_LIMITS.books.language}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={formatCategories(item.category)}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                            updateImportItem(item.id, 'category', e.target.value)
                                                        }
                                                        className="import-input"
                                                        list="category-options"
                                                        style={{ minWidth: '10ch' }}
                                                        title={formatCategories(item.category)}
                                                        maxLength={FIELD_LIMITS.books.category}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={item.publisher || ''}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => updateImportItem(item.id, 'publisher', e.target.value)}
                                                        className="import-input"
                                                        list="publisher-options"
                                                        style={{ minWidth: '10ch' }}
                                                        title={item.publisher || ''}
                                                        maxLength={FIELD_LIMITS.books.publisher}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={item.series || ''}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => updateImportItem(item.id, 'series', e.target.value)}
                                                        className="import-input"
                                                        list="series-options"
                                                        style={{ minWidth: '15ch' }}
                                                        title={item.series || ''}
                                                        maxLength={FIELD_LIMITS.books.series}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={item.series_number !== null && item.series_number !== undefined ? String(item.series_number) : ''}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                            updateImportItem(item.id, 'series_number', String(e.target.value))
                                                        }
                                                        className="import-input"
                                                        style={{ width: '60px' }}
                                                        title={item.series_number !== null && item.series_number !== undefined ? String(item.series_number) : ''}
                                                    />
                                                </td>
                                                <td>
                                                    <select
                                                        value={item.format || ''}
                                                        onChange={(e: ChangeEvent<HTMLSelectElement>) => updateImportItem(item.id, 'format', e.target.value)}
                                                        className="import-select"
                                                        style={{ minWidth: '10ch' }}
                                                        title={item.format || ''}
                                                    >
                                                        <option value="">--</option>
                                                        {FORMAT_OPTIONS.map((opt) => (
                                                            <option key={opt} value={opt}>
                                                                {opt}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td>
                                                    <select
                                                        value={item.status || ''}
                                                        onChange={(e: ChangeEvent<HTMLSelectElement>) => updateImportItem(item.id, 'status', e.target.value)}
                                                        className="import-select"
                                                        style={{ minWidth: '10ch' }}
                                                        title={item.status || ''}
                                                    >
                                                        <option value="">--</option>
                                                        {STATUS_OPTIONS.map((opt) => (
                                                            <option key={opt} value={opt}>
                                                                {opt}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td>
                                                    <select
                                                        value={item.progress || ''}
                                                        onChange={(e: ChangeEvent<HTMLSelectElement>) => updateImportItem(item.id, 'progress', e.target.value)}
                                                        className="import-select"
                                                        style={{ minWidth: '10ch' }}
                                                        title={item.progress || ''}
                                                    >
                                                        <option value="">--</option>
                                                        {PROGRESS_OPTIONS.map((opt) => (
                                                            <option key={opt} value={opt}>
                                                                {opt}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={item.location || ''}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => updateImportItem(item.id, 'location', e.target.value)}
                                                        className="import-input"
                                                        list="location-options"
                                                        style={{ minWidth: '10ch' }}
                                                        title={item.location || ''}
                                                        maxLength={FIELD_LIMITS.books.location}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={item.notes || ''}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => updateImportItem(item.id, 'notes', e.target.value)}
                                                        className="import-input"
                                                        style={{ minWidth: '15ch' }}
                                                        title={item.notes || ''}
                                                        maxLength={FIELD_LIMITS.books.notes}
                                                    />
                                                </td>
                                            </tr>
                                            {matchingBooks.map((existingBook) => (
                                                <tr key={`${item.id}-match-${existingBook.id}`} className="row-existing-preview">
                                                    <td className="td-actions"></td>
                                                    <td className="td-status">
                                                        {formatAuthors(existingBook.author).toLowerCase().trim() ===
                                                            formatAuthors(item.author).toLowerCase().trim() ? (
                                                            <span className="status-icon danger" title="Duplicado exacto">
                                                                ❗
                                                            </span>
                                                        ) : (
                                                            <span className="status-icon warning" title="Mismo título">
                                                                ❓
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="preview-label" title={existingBook.title || ''}>
                                                        {existingBook.title}
                                                    </td>
                                                    <td className="preview-label" title={formatAuthors(existingBook.author)}>
                                                        {formatAuthors(existingBook.author)}
                                                    </td>
                                                    <td className="preview-label" title={existingBook.language || ''}>
                                                        {existingBook.language || '-'}
                                                    </td>
                                                    <td className="preview-label" title={formatCategories(existingBook.category)}>
                                                        {formatCategories(existingBook.category)}
                                                    </td>
                                                    <td className="preview-label" title={existingBook.publisher || ''}>
                                                        {existingBook.publisher || '-'}
                                                    </td>
                                                    <td className="preview-label" title={existingBook.series || ''}>
                                                        {existingBook.series || '-'}
                                                    </td>
                                                    <td className="preview-label" title={existingBook.series_number ? String(existingBook.series_number) : ''}>
                                                        {existingBook.series_number || '-'}
                                                    </td>
                                                    <td className="preview-label" title={existingBook.format || ''}>
                                                        {existingBook.format || '-'}
                                                    </td>
                                                    <td className="preview-label" title={existingBook.status || ''}>
                                                        {existingBook.status || '-'}
                                                    </td>
                                                    <td className="preview-label" title={existingBook.progress || ''}>
                                                        {existingBook.progress || '-'}
                                                    </td>
                                                    <td className="preview-label" title={existingBook.location || ''}>
                                                        {existingBook.location || '-'}
                                                    </td>
                                                    <td className="preview-label" title={existingBook.notes || ''}>
                                                        {existingBook.notes || '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </Fragment>
                                    )
                                })}
                            </tbody>
                        </table>

                        {/* Datalists for autocomplete */}
                        <datalist id="author-options">
                            {[...new Set(books.flatMap((b) => b.author || []))]
                                .sort((a, b) => a.localeCompare(b))
                                .map((author) => (
                                    <option key={author} value={author} />
                                ))}
                        </datalist>
                        <datalist id="language-options">
                            {existingLanguages.map((lang) => (
                                <option key={lang} value={lang} />
                            ))}
                        </datalist>
                        <datalist id="category-options">
                            {existingCategories.map((cat) => (
                                <option key={cat} value={cat} />
                            ))}
                        </datalist>
                        <datalist id="publisher-options">
                            {existingPublishers.map((pub) => (
                                <option key={pub} value={pub} />
                            ))}
                        </datalist>
                        <datalist id="series-options">
                            {existingSeries.map((ser) => (
                                <option key={ser} value={ser} />
                            ))}
                        </datalist>
                        <datalist id="location-options">
                            {existingLocations.map((loc) => (
                                <option key={loc} value={loc} />
                            ))}
                        </datalist>
                    </div>
                </div>
                <div className="modal-footer">
                    <span className="import-count">{importData.length} libros a importar</span>
                    <div className="footer-buttons">
                        <button className="btn-secondary" onClick={onClose} disabled={isImporting} data-testid="cancel-import-btn">
                            Cancelar
                        </button>
                        <button className="btn-primary" onClick={handleImportConfirm} disabled={isImporting || importData.length === 0} data-testid="confirm-import-btn">
                            {isImporting ? 'Importando...' : 'Confirmar Importar'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
